import { Stack } from '@mui/material';
import { useForm } from 'react-typed-form';
import { useParams } from 'react-router';
import PublishingSection from '../../components/PublishingSection';
import { UnsavedChangesBlocker } from '../../components';
import { usePublishing } from '../../hooks';
import { Api, useApiCallable } from '../../api';
import { FieldNumber, FieldText, FieldTextRequired } from '../../form';

type Props = {
  existing?: Api.PsychologyPrompt.V_DetailAsAdmin;
};

export default function PsychologyPromptForm({ existing }: Props) {
  const callApi = useApiCallable();
  const { weekId } = useParams();

  const publishing = usePublishing(
    {
      contentType: 'Psychology Prompt',
      apiPath: 'psychology-prompts',
      existingId: existing?.id,
    },
    () => form.handleSubmit(),
  );

  const initialValues = {
    title: existing?.title ?? '',
    position: existing?.position ?? 1,
    description: existing?.description ?? '',
    week: `/program-weeks/${weekId}`,
  };

  const form = useForm<{
    title: string;
    position: number | null;
    description: string | null;
    week: string;
  }>({
    defaultValues: initialValues,
    onSubmit: async (values, form) => {
      const { setLoading, addSubmitError } = form;
      let response;

      if (!existing) {
        setLoading(true);
        response = await callApi(
          `/psychology-prompts`,
          {
            jsonBody: values,
            method: 'POST',
          },
          {
            addSubmitError,
          },
        );
      } else {
        response = await callApi(
          `/psychology-prompts/${existing.id}`,
          {
            method: 'PUT',
            jsonBody: values,
          },
          {
            addSubmitError,
          },
        );
      }

      setLoading(false);

      if (!response) return;

      await publishing.handleSubmitSuccess(response, !!existing);
    },
  });

  return (
    <Stack gap={3}>
      <Stack gap={3} maxWidth={600}>
        <FieldTextRequired
          field={form.getField('title')}
          label="Prompt"
          helperText="The title will be displayed on the app"
        />
        <FieldText
          field={form.getField('description')}
          label="Description"
          helperText="This can be examples or further instructions"
        />
        <FieldNumber
          field={form.getField('position')}
          label="Position"
          helperText="e.g. 1 for the first prompt that will be shown to the user"
          required
        />
      </Stack>
      <PublishingSection
        existing={existing}
        isLoading={form.isLoading}
        contentType="Psychology Prompt"
        apiPath="psychology-prompts"
        publishing={publishing}
      />

      <UnsavedChangesBlocker
        initialValues={initialValues}
        currentValues={form.values}
        isSubmittingRef={publishing.isSubmittingRef}
      />
    </Stack>
  );
}
